'use client';
import Logo from '@/components/global/Logo';
import { Motion } from '@/components/global/utils/Motion';
import { GroqImage } from '@/helpers/Image';
import { GROQButton, GROQImage } from '@/sanity/lib/definitions';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { motion, useScroll, useTransform } from 'framer-motion';
import { PortableText } from 'next-sanity';
import { useRef } from 'react';
import { useIsMobile } from '@/hooks/useIsMobile';
import { CTAButton } from '@/shadcn/ui/button';

interface ICommonQuestionsProps {
  label: string;
  title: any; // Assuming this is a PortableText block
  button: GROQButton;
  image: GROQImage;
  questionOne: any; // Assuming this is a PortableText block
  questionTwo: any; // Assuming this is a PortableText block
  questionThree: any; // Assuming this is a PortableText block
}

export default function CommonQuestions({
  label,
  title,
  button,
  image,
  questionOne,
  questionTwo,
  questionThree,
}: ICommonQuestionsProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const isMobile = useIsMobile();

  // Helper to bold first three words
  const formatLabel = (text: string) => {
    const words = text.split(' ');
    const firstThree = words.slice(0, 3).join(' ');
    const rest = words.slice(3).join(' ');

    return (
      <span>
        <span className="font-bold">{firstThree}</span>{' '}
        <span className="opacity-70">{rest && `${rest}`}</span>
      </span>
    );
  };

  // Track scroll progress of the section
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });

  // Transform scroll progress to translateY
  // Start parallax effect only after 50% scroll (0.5)
  // Map from 0-0.5 scroll progress to 0 translateY (no movement)
  // Then from 0.5-1 scroll progress to 0-500px translateY
  const sectionY = useTransform(scrollYProgress, [0, 0.5, 1], [0, 0, 500]);

  return (
    <motion.section
      ref={sectionRef}
      style={{ y: isMobile ? 0 : sectionY }}
      className="pt-[15rem] pb-[8rem] lg:py-[38rem]"
    >
      <div className="container flex lg:gap-0 gap-[4rem] flex-wrap items-stretch justify-between">
        <div className="lg:w-[50rem] lg:gap-0 gap-[6rem] flex flex-col">
          <Logo className="lg:w-[13.5rem] w-[9.6rem]" />

          <motion.div
            initial={{ clipPath: 'inset(0% 100% 0% 0%)' }}
            whileInView={{ clipPath: 'inset(0% 0% 0% 0%)' }}
            transition={{ duration: 1.2, ease: [0.25, 1, 0.5, 1] }}
            className="flex flex-col mt-auto"
          >
            <p className="font-nunito text-offwhite text-[1.75rem] lg:text-[2rem] font-[300]  flex flex-wrap items-center gap-[2rem]">
              <span className="block h-[.125rem] bg-[#8A8F93] w-[10rem] lg:w-[5.5rem]"></span>
              {formatLabel(label)}
            </p>
            <div className="flex flex-col gap-[3.5rem] text-offwhite mt-[4rem] lg:mb-[2rem]">
              {title && (
                <PortableText value={title} components={richTextComponent} />
              )}
            </div>
            {button && (
              <CTAButton className="hidden lg:block" data={button} />
            )}
          </motion.div>
        </div>
        <motion.div
          initial={{ clipPath: 'inset(0% 100% 0% 0%)' }}
          whileInView={{ clipPath: 'inset(0% 0% 0% 0%)' }}
          transition={{ duration: 1.2, ease: [0.25, 1, 0.5, 1] }}
          className="w-full lg:w-[75.25rem] lg:min-h-[113.125rem] relative overflow-clip"
        >
          {image && (
            <GroqImage
              src={image}
              alt="Common Questions Image"
              className="!w-full !h-full !object-cover object-center"
            />
          )}
        </motion.div>
        <motion.div
          initial={{ clipPath: 'inset(0% 100% 0% 0%)' }}
          whileInView={{ clipPath: 'inset(0% 0% 0% 0%)' }}
          transition={{ duration: 1.2, ease: [0.25, 1, 0.5, 1] }}
          className="lg:w-[56.5rem] flex flex-col gap-[3rem]"
        >
          <div className="py-[4rem] prose-h3:text-[3rem] px-[3rem] flex flex-col gap-[5rem] prose-p:text-[#f2f0ef] prose-p:opacity-70 prose-h3:text-[#f2f0ef] prose-p:leading-[160%] prose-h3:leading-[130%]">
            {questionOne && (
              <PortableText
                value={questionOne}
                components={richTextComponent}
              />
            )}
          </div>

          <div className="py-[4rem] prose-h3:text-[3rem] px-[3rem] flex flex-col gap-[5rem] prose-p:text-[#f2f0ef] prose-p:opacity-70 prose-h3:text-[#f2f0ef] prose-p:leading-[160%] prose-h3:leading-[130%] border-t-[rgba(255,255,255,0.12)] border-b-[rgba(255,255,255,0.12)] border-[.125rem] border-l-0 border-r-0">
            {questionTwo && (
              <PortableText
                value={questionTwo}
                components={richTextComponent}
              />
            )}
          </div>

          <div className="py-[4rem] prose-h3:text-[3rem] px-[3rem] flex flex-col gap-[5rem] prose-p:text-[#f2f0ef] prose-p:opacity-70 prose-h3:text-[#f2f0ef] prose-p:leading-[160%] prose-h3:leading-[130%]">
            {questionThree && (
              <PortableText
                value={questionThree}
                components={richTextComponent}
              />
            )}
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
}
